import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	QueryGroup,
	QueryCondition,
	isQueryGroup,
	createEmptyGroup,
	createEmptyCondition,
	generateId
} from '@/types/queryBuilder';
import { executeQuery, countResults, serializeQueryToURL, deserializeQueryFromURL } from '@/utils/queryBuilder';
import { Candidate } from '@/types/candidate';

export function useQueryBuilder() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [rootGroup, setRootGroup] = useState<QueryGroup>(() => {
		// Try to load from URL on initial mount
		const queryParam = searchParams.get('q');
		if (queryParam) {
			const deserialized = deserializeQueryFromURL(queryParam);
			if (deserialized)
				return deserialized;
		}
		return createEmptyGroup('AND');
	});
	const [results, setResults] = useState<Candidate[]>([]);
	const [resultsCount, setResultsCount] = useState<number>(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isCountLoading, setIsCountLoading] = useState(false);
	const [hasRun, setHasRun] = useState(false);

	// Sync rootGroup to URL whenever it changes
	useEffect(() => {
		const serialized = serializeQueryToURL(rootGroup);
		const currentParam = searchParams.get('q') || '';

		// Only update if the value has changed
		if (serialized && serialized !== currentParam)
			setSearchParams({ q: serialized }, { replace: true });
		else if (!serialized && currentParam)
			setSearchParams({}, { replace: true });
	}, [rootGroup, searchParams, setSearchParams]);

	// Find and update item in tree
	const findAndUpdate = useCallback((
		group: QueryGroup,
		targetId: string,
		updater: (item: QueryCondition | QueryGroup) => QueryCondition | QueryGroup | null
	): QueryGroup => {
		const newConditions: (QueryCondition | QueryGroup)[] = [];

		for (const item of group.conditions) {
			if (item.id === targetId) {
				const updated = updater(item);
				if (updated !== null)
					newConditions.push(updated);
			} else if (isQueryGroup(item)) {
				newConditions.push(findAndUpdate(item, targetId, updater));
			} else {
				newConditions.push(item);
			}
		}

		return { ...group, conditions: newConditions };
	}, []);

	// Find parent group and add item
	const findAndAddTo = useCallback((
		group: QueryGroup,
		parentId: string,
		newItem: QueryCondition | QueryGroup
	): QueryGroup => {
		if (group.id === parentId)
			return { ...group, conditions: [...group.conditions, newItem] };

		const newConditions = group.conditions.map(item => {
			if (isQueryGroup(item))
				return findAndAddTo(item, parentId, newItem);
			return item;
		});

		return { ...group, conditions: newConditions };
	}, []);

	// Add condition to a group
	const addCondition = useCallback((parentGroupId: string) => {
		const newCondition = createEmptyCondition();
		setRootGroup(prev => findAndAddTo(prev, parentGroupId, newCondition));
	}, [findAndAddTo]);

	// Update condition
	const updateCondition = useCallback((conditionId: string, updates: Partial<QueryCondition>) => {
		setRootGroup(prev => findAndUpdate(prev, conditionId, (item) => {
			if (!isQueryGroup(item))
				return { ...item, ...updates } as QueryCondition;
			return item;
		}));
	}, [findAndUpdate]);

	// Remove condition
	const removeCondition = useCallback((conditionId: string) => {
		setRootGroup(prev => findAndUpdate(prev, conditionId, () => null));
	}, [findAndUpdate]);

	// Add nested group
	const addGroup = useCallback((parentGroupId: string, groupType: 'AND' | 'OR') => {
		const newGroup = createEmptyGroup(groupType);
		setRootGroup(prev => findAndAddTo(prev, parentGroupId, newGroup));
	}, [findAndAddTo]);

	// Update group type (AND/OR)
	const updateGroupType = useCallback((groupId: string, type: 'AND' | 'OR') => {
		setRootGroup(prev => {
			// Handle root group case
			if (prev.id === groupId)
				return { ...prev, type };

			// Handle nested groups
			return findAndUpdate(prev, groupId, (item) => {
				if (isQueryGroup(item))
					return { ...item, type };
				return item;
			});
		});
	}, [findAndUpdate]);

	// Remove group
	const removeGroup = useCallback((groupId: string) => {
		setRootGroup(prev => findAndUpdate(prev, groupId, () => null));
	}, [findAndUpdate]);

	// Run query
	const runQuery = useCallback(async () => {
		setIsLoading(true);
		setHasRun(true);
		try {
			const data = await executeQuery(rootGroup);
			setResults(data);
			setResultsCount(data.length);
		} catch (error) {
			console.error('Query execution failed:', error);
			setResults([]);
			setResultsCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [rootGroup]);

	// Preview count (without fetching all data)
	const previewCount = useCallback(async () => {
		setIsCountLoading(true);
		try {
			const count = await countResults(rootGroup);
			setResultsCount(count);
		} catch (error) {
			console.error('Count failed:', error);
			setResultsCount(0);
		} finally {
			setIsCountLoading(false);
		}
	}, [rootGroup]);

	// Load a saved query
	const loadQuery = useCallback((group: QueryGroup) => {
		setRootGroup(group);
		setResults([]);
		setResultsCount(0);
		setHasRun(false);
	}, []);

	// Clear query
	const clearQuery = useCallback(() => {
		setRootGroup(createEmptyGroup('AND'));
		setResults([]);
		setResultsCount(0);
		setHasRun(false);
		setSearchParams({}, { replace: true });
	}, [setSearchParams]);

	// Get current query definition (for saving)
	const getQueryDefinition = useCallback(() => ({
		rootGroup,
		targetEntity: 'candidate' as const
	}), [rootGroup]);

	return {
		rootGroup,
		results,
		resultsCount,
		isLoading,
		isCountLoading,
		hasRun,
		addCondition,
		updateCondition,
		removeCondition,
		addGroup,
		updateGroupType,
		removeGroup,
		runQuery,
		previewCount,
		loadQuery,
		clearQuery,
		getQueryDefinition
	};
}
